import { type RefObject, useEffect, useLayoutEffect } from "react";

/**
 * Auto-resizes a textarea to fit its content height.
 *
 * When `wrapperRef` is provided, the wrapper's height is set to the textarea's
 * scrollHeight and the textarea fills it at 100%. Without a wrapper, the
 * textarea's height is set directly.
 *
 * A `ResizeObserver` re-adjusts on width changes (e.g. container resize or
 * becoming visible), avoiding a resize-observer loop by only firing when the
 * width actually changes.
 */
export function useAutoResizeTextarea(params: {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  wrapperRef?: RefObject<HTMLElement | null>;
  value: string;
  isEnabled?: boolean;
}): void {
  const { textareaRef, wrapperRef, value, isEnabled: enabled = true } = params;

  // Adjust height synchronously after value changes to avoid flicker.
  useLayoutEffect(() => {
    if (!enabled) return;

    adjustTextareaHeight(textareaRef, wrapperRef);
  }, [enabled, value, textareaRef, wrapperRef]);

  // Re-adjust when the textarea's width changes (including when it first
  // becomes visible).
  useEffect(() => {
    if (!enabled) return;

    const textareaElement = textareaRef.current;
    if (!textareaElement) return;

    let width: number | undefined = undefined;

    const observer = new ResizeObserver((records) => {
      const prevWidth = width;
      const nextWidth = records.at(0)?.contentRect.width;
      width = nextWidth;

      // Adjust only when the width changed to avoid a resize observer loop.
      if (nextWidth && prevWidth !== nextWidth) {
        adjustTextareaHeight(textareaRef, wrapperRef);
      }
    });

    observer.observe(textareaElement);

    return () => {
      observer.disconnect();
    };
  }, [enabled, textareaRef, wrapperRef]);
}

function adjustTextareaHeight(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  wrapperRef?: RefObject<HTMLElement | null>,
): void {
  const textareaElement = textareaRef.current;
  if (!textareaElement) return;

  // Reset to auto so it can shrink or grow.
  textareaElement.style.height = "auto";

  const wrapperElement = wrapperRef?.current;
  if (wrapperElement) {
    // Set wrapper to the intrinsic content height, then let the textarea fill it.
    wrapperElement.style.height = `${textareaElement.scrollHeight}px`;
    textareaElement.style.height = "100%";
  } else {
    textareaElement.style.height = `${textareaElement.scrollHeight}px`;
  }
}
